import {fetchWithRetry} from '@modules/http/fetchWithRetry';

import type {PlaylistSummary} from './types';

export type FetchPlaylistsOptions = {
	fetchImpl?: typeof fetch;
	signal?: AbortSignal;
};

/**
 * Client-side wrapper around `GET /api/playlists`. The server route is
 * the single point of contact with Deezer (so the partner auth token
 * never reaches the browser). Throws on non-2xx.
 *
 * Returns `PlaylistSummary[]` — no per-track data. ISRCs and full
 * track lists stay server-side; analyze runs on the server too. See
 * docs/specs/product-feedback.md entry `2026-04-29 ·
 * api-playlists-leaks-isrc`.
 *
 * Retries transient failures (network drop, 5xx, 408, 429) with
 * exponential backoff via `fetchWithRetry`. 4xx errors fail fast.
 */
export async function fetchPlaylistsForUser(
	opts: FetchPlaylistsOptions = {},
): Promise<PlaylistSummary[]> {
	const f = opts.fetchImpl ?? ((input, init) => fetchWithRetry(input, init));
	const res = await f('/api/playlists', {
		method: 'GET',
		credentials: 'include',
		signal: opts.signal,
	});
	if (!res.ok) {
		let errorCode: string | undefined;
		try {
			const errBody = (await res.json()) as {code?: string};
			errorCode = typeof errBody.code === 'string' ? errBody.code : undefined;
		} catch {
			// ignore parse failure — fall back to status-based code
		}
		throw new Error(errorCode ?? `fetch_playlists_failed_${res.status}`);
	}
	const data = (await res.json()) as {playlists?: PlaylistSummary[]};
	if (!Array.isArray(data.playlists)) {
		throw new Error('fetch_playlists_invalid_response');
	}
	return data.playlists;
}
